import { useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, Loader2, CheckCircle2, Search } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v1/compliance/privacy/public`;
const TYPES = [
  { v: "access", l: "Access my data" },
  { v: "export", l: "Export my data" },
  { v: "portability", l: "Data portability" },
  { v: "erasure", l: "Delete my data (erasure)" },
  { v: "rectification", l: "Correct my data" },
  { v: "objection", l: "Object to processing" },
];

export default function PublicPrivacyRequest() {
  const { tenantId } = useParams();
  const [tab, setTab] = useState("submit");
  const [form, setForm] = useState({ type: "access", email: "", name: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [statusToken, setStatusToken] = useState("");
  const [statusResult, setStatusResult] = useState(null);

  const submit = async () => {
    setError(""); setSubmitting(true);
    try {
      const res = await axios.post(`${BASE}/${tenantId}/request`, form);
      setResult(res.data.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not submit request");
    } finally { setSubmitting(false); }
  };

  const checkStatus = async () => {
    setError(""); setStatusResult(null);
    try {
      const res = await axios.get(`${BASE}/${statusToken.trim()}`);
      setStatusResult(res.data.data);
    } catch (e) {
      setError(e.response?.data?.detail || "No request found for this reference");
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-4" data-testid="public-privacy-page">
      <div className="w-full max-w-lg bg-slate-900 border border-slate-800 rounded-2xl p-7 shadow-2xl">
        <div className="flex items-center gap-3 mb-1">
          <span className="h-10 w-10 rounded-xl bg-teal-500/15 text-teal-400 grid place-items-center"><ShieldCheck className="h-5 w-5" /></span>
          <h1 className="text-xl font-bold">Privacy Request Portal</h1>
        </div>
        <p className="text-sm text-slate-400 mb-5">Submit a data-subject request or check the status of an existing one.</p>

        <div className="flex gap-2 mb-5">
          <button onClick={() => setTab("submit")} className={`flex-1 rounded-lg px-3 py-2 text-sm ${tab === "submit" ? "bg-teal-600 text-white" : "bg-slate-800 text-slate-300"}`} data-testid="tab-submit">New request</button>
          <button onClick={() => setTab("status")} className={`flex-1 rounded-lg px-3 py-2 text-sm ${tab === "status" ? "bg-teal-600 text-white" : "bg-slate-800 text-slate-300"}`} data-testid="tab-status">Check status</button>
        </div>

        {error && <div className="mb-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm px-3 py-2" data-testid="public-error">{error}</div>}

        {tab === "submit" && (result ? (
          <div className="text-center py-6" data-testid="submit-success">
            <CheckCircle2 className="h-12 w-12 text-teal-400 mx-auto mb-3" />
            <h2 className="text-lg font-semibold">Request submitted</h2>
            <p className="text-sm text-slate-400 mt-1">Reference: <span className="font-mono text-teal-300">{result.requestNumber}</span></p>
            <p className="text-xs text-slate-500 mt-2">Save this token to check status:</p>
            <p className="font-mono text-xs bg-slate-800 rounded-lg px-3 py-2 mt-1 break-all" data-testid="result-token">{result.token}</p>
            <p className="text-xs text-slate-500 mt-3">We will respond by {new Date(result.dueDate).toLocaleDateString()}.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm" data-testid="public-type-select">
              {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
            <input placeholder="Your email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm" data-testid="public-email-input" />
            <input placeholder="Your name (optional)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm" />
            <textarea placeholder="Additional details (optional)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm" rows={3} />
            <button onClick={submit} disabled={submitting || !form.email} className="w-full rounded-lg bg-teal-600 hover:bg-teal-700 text-white py-2.5 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2" data-testid="public-submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Submit request
            </button>
          </div>
        ))}

        {tab === "status" && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <input placeholder="Enter your reference token" value={statusToken} onChange={(e) => setStatusToken(e.target.value)}
                className="flex-1 rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm" data-testid="status-token-input" />
              <button onClick={checkStatus} disabled={!statusToken.trim()} className="rounded-lg bg-teal-600 hover:bg-teal-700 text-white px-3 py-2 text-sm disabled:opacity-50 flex items-center gap-2" data-testid="check-status-btn">
                <Search className="h-4 w-4" /> Check
              </button>
            </div>
            {statusResult && (
              <div className="rounded-lg bg-slate-800 border border-slate-700 p-4 text-sm" data-testid="status-result">
                <div className="flex justify-between py-1"><span className="text-slate-400">Reference</span><span className="font-mono text-teal-300">{statusResult.requestNumber}</span></div>
                <div className="flex justify-between py-1"><span className="text-slate-400">Type</span><span className="capitalize">{statusResult.type}</span></div>
                <div className="flex justify-between py-1"><span className="text-slate-400">Status</span><span className="font-semibold">{statusResult.status}</span></div>
                <div className="flex justify-between py-1"><span className="text-slate-400">Due by</span><span>{new Date(statusResult.dueDate).toLocaleDateString()}</span></div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
